import type { ComponentProps, ReactNode, Ref } from 'react';

import {
  ScrollAreaCorner,
  ScrollArea as ScrollAreaPrimitive,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from '../../primitives';
import { cn } from '../../shared';

export interface OverlayScrollAreaProps extends ComponentProps<typeof ScrollAreaPrimitive> {
  /** Which axes this instance can scroll. Radix only clips an axis with no matching `Scrollbar`
   * mounted — leaving both on a caller that only ever overflows vertically, say, lets a wide
   * descendant's own scroll-content measurement pass straight through as real overflow here too,
   * so both this area and the descendant end up scrollable on that axis at once. */
  orientation?: 'both' | 'horizontal' | 'vertical';
  viewportClassName?: string;
  /** Points at the viewport, not this component's own root — the viewport is the element whose
   * `clientWidth`/`clientHeight` excludes the (hidden) native scrollbar, same as a plain
   * `overflow-x-auto` div's would. */
  viewportRef?: Ref<HTMLDivElement>;
  /** Overrides the shared `h-scrollbar` thickness for this instance's horizontal track only. */
  horizontalScrollbarClassName?: string;
  /** Overrides the shared `w-scrollbar` thickness for this instance's vertical track only. */
  verticalScrollbarClassName?: string;
  /** Radix sizes the thumb itself via an inline `width` reflecting the viewport/content ratio —
   * a plain `max-w-*` here still clamps it, since max-width is a separate property from the one
   * that inline style sets and always wins the clamp regardless of which side set which. */
  horizontalThumbClassName?: string;
  /** Same as `horizontalThumbClassName`, but for the inline `height` Radix sets on this axis. */
  verticalThumbClassName?: string;
  children: ReactNode;
}

// Radix sets `position: absolute` on the scrollbar inline but leaves the offsets to the
// caller — without `inset-x-0 bottom-0`/`inset-y-0 right-0` here, it renders at its own
// hypothetical static position instead of pinned flush to the edge it's meant to overlay.
// `px-`/`py-scrollbar-inset` below is one-axis-only for the same reason: padding on the axis
// it's pinned to would push the thumb back off the edge `inset-*`/`bottom-0`/`right-0` just set.
const scrollbarClassName =
  'flex touch-none select-none transition-opacity duration-fast data-[state=hidden]:opacity-0 data-[state=visible]:opacity-100';

/**
 * The scrollbar is Radix's own overlay `Scrollbar`/`Thumb`, not the browser's native one —
 * `Viewport` hides its own native scrollbar behind a negative margin and this root's
 * `overflow-hidden`, so nothing here ever reserves layout space or fights a rounded corner the
 * way a plain `overflow-x-auto` div's real scrollbar does.
 */
export function OverlayScrollArea({
  className,
  orientation = 'both',
  viewportClassName,
  viewportRef,
  horizontalScrollbarClassName,
  verticalScrollbarClassName,
  horizontalThumbClassName,
  verticalThumbClassName,
  children,
  ...props
}: OverlayScrollAreaProps) {
  const showHorizontal = orientation === 'both' || orientation === 'horizontal';
  const showVertical = orientation === 'both' || orientation === 'vertical';

  // Radix wraps `children` in its own `display: table` div to measure content size, which sizes
  // to whatever the content wants regardless of the viewport's own (already-definite) size —
  // fine for an axis this area scrolls, but for one it doesn't, that demand has nowhere to go
  // but back up through the viewport into whatever laid *this* area out. `contain: inline-size`
  // stops a vertical-only area from making that contribution on the inline axis, so a wide
  // descendant clips instead of forcing its own ancestors wider.
  //
  // There is deliberately no mirror of this for `orientation: 'horizontal'`. CSS containment has
  // no single-axis value for the block axis — `contain` accepts `size` and `inline-size`, and
  // nothing else that would contain height alone (verified against the browser: setting
  // `contain: block-size` is rejected outright and computes to `none`). An earlier revision did
  // set `[contain:block-size]` here; it was silently doing nothing, which is worse than not
  // trying, because the class read as protection that was not there. `contain: size` is not a
  // substitute: it contains *both* axes, so a horizontal area would have to be given an explicit
  // height, and DataGrid's whole layout depends on the table's own height bubbling up (it scrolls
  // sideways, never vertically — the page owns vertical scrolling).
  const containment = orientation === 'vertical' ? '[contain:inline-size]' : undefined;

  return (
    <ScrollAreaPrimitive type="hover" className={cn('overflow-hidden', className)} {...props}>
      <ScrollAreaViewport ref={viewportRef} className={cn('h-full w-full', viewportClassName)}>
        {containment ? <div className={containment}>{children}</div> : children}
      </ScrollAreaViewport>
      {showHorizontal ? (
        <ScrollAreaScrollbar
          orientation="horizontal"
          className={cn(
            scrollbarClassName,
            'inset-x-0 bottom-0 h-scrollbar flex-col px-scrollbar-inset',
            horizontalScrollbarClassName,
          )}
        >
          <ScrollAreaThumb
            className={cn(
              'bg-scrollbar-thumb relative max-w-scrollbar-thumb flex-1 rounded-full',
              horizontalThumbClassName,
            )}
          />
        </ScrollAreaScrollbar>
      ) : null}
      {showVertical ? (
        <ScrollAreaScrollbar
          orientation="vertical"
          className={cn(
            scrollbarClassName,
            'inset-y-0 right-0 w-scrollbar py-scrollbar-inset',
            verticalScrollbarClassName,
          )}
        >
          <ScrollAreaThumb
            className={cn(
              'bg-scrollbar-thumb relative max-h-scrollbar-thumb flex-1 rounded-full',
              verticalThumbClassName,
            )}
          />
        </ScrollAreaScrollbar>
      ) : null}
      <ScrollAreaCorner />
    </ScrollAreaPrimitive>
  );
}
